import React, {
  ChangeEvent,
  FC,
  ReactNode,
} from 'react';

import Color from 'color'
import { motion } from 'motion/react';
import { Stack, styled } from '@mui/material';

import {
  Button,
  Card,
  Container,
  DatePicker,
  Heading,
  Input,
  LicenseInput,
  Paragraph,
  SubmitButton,
} from '@domain/components';
import { ContentStructureFormBlock, FormDataCreation, InputName } from '@domain/interfaces';
import { Colors } from '@domain/theming';
import { Scaling } from '@domain/theming'
import { useWindowSize } from '@domain/hooks';
import { useSelector } from 'react-redux';
import { RootState } from '@domain/root-reducer';

const cardGradient = 'linear-gradient(342deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.18) 37%, rgba(0,0,0,0.08) 91%)';

const FormCard = styled(motion.create(Card) as any, {
  shouldForwardProp: (propName) => !propName.toString().startsWith('$')
})`
   background-image: ${cardGradient};
        border: 3px solid ${Colors.WHITE};
        border-radius: 0px;
        flex-grow: 0;
        width: 100%;
        position:relative;
        max-width: ${props => Scaling.scaleUpTo4K(520, props.$vwWidth)}px;
        padding: 0;
        margin: auto;
        align-self: flex-start;
        align-items: flex-end;
        opacity: .85;
        will-change: transform, opacity;
        &.card-form-grey {
          background-image: linear-gradient(
            358deg,
            ${Colors.GREY_LIGHT} -10%,
            ${Color(Colors.GREY_LIGHT).lighten(0.06).hex()} 33%,
            ${Colors.GREY_LIGHT} 55%
            );
            .button {
              border-color: ${Colors.BLACK};
              color: ${Colors.BLACK};
              &:hover {
                background: ${Color(Colors.BLACK).rgb().alpha(0.08).string()}
              }
            }
        }
    
        &.single-card {
          min-height:  ${props => Scaling.scaleUpTo4K(468, props.$vwWidth)}px;
          height: 0; //IE
        }
        + .card {
          margin-top: ${props => Scaling.scaleUpTo4K(60, props.$vwWidth)}px;
          margin-bottom: ${props => Scaling.scaleUpTo4K(468, props.$vwWidth)}px;
        }
        p, h3, input, label {
        }
        @media screen and (min-width:1920px) {
          &.large-width-out {
            max-width: ${props => Scaling.scaleUpTo4K(650, props.$vwWidth)}px;
          }
        }
        @media screen and (max-width: 900px) {
          + .card {
            margin-top: 40px;
            margin-bottom:30px;
          }
        }
        @media screen and (max-width: 420px) {
          &.card.card-form {
            padding: 0;
          }
        }     
      `;

const FormCardContainer = styled(
  motion.create(Container) as any, {
  shouldForwardProp: (propName) => !propName.toString().startsWith('$')
}
)`
        padding: ${props => Scaling.scaleUpTo4K(30, props.$vwWidth)}px ${props => Scaling.scaleUpTo4K(30, props.$vwWidth)}px ${props => Scaling.scaleUpTo4K(40, props.$vwWidth)}px;
        will-change: transform, opacity;
        + .card-form__container--response {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }
        .card-form__button--redirect {
          max-width: 100%;
          text-align: center;
          + .card-form__button--redirect {
            margin-top: ${props => Scaling.scaleUpTo4K(30, props.$vwWidth)}px;
          }
        }
        h4, h5 {
          // color: ${props => props.theme.palette.primary.main};
          color: ${props => props.theme.isCustomThemed ? Colors.GREY_DARKER : props.theme.palette.primary.contrastText};
        }
      `;

const CaseForm = styled(motion.div as any, {
  shouldForwardProp: (propName) => !propName.toString().startsWith('$')
})`
      display: flex;
      flex-direction: column;
      flex: 1 0 auto;
      max-width: ${props => Scaling.scaleUpTo4K(500, props.$vwWidth)}px;
      padding: 0;
      // justify-content: space-between;
      .card-form & .button--submit {
        margin-top: auto;
      }
      > p,
      > ul,
      > h3 {
        max-width: 100%;
      }
      ul {
        padding-inline-start: 0px;
        padding-inline-end: 0px;
        padding-left: ${props => Scaling.scaleUpTo4K(18, props.$vwWidth)}px;
        padding-bottom: ${props => Scaling.scaleUpTo4K(15, props.$vwWidth)}px;
        font-size: ${props => Scaling.textFontSize(13, props.$vwWidth)}px;
      }
      @media screen and (min-width:1920px) {
        &.large-width {
          max-width: ${props => Scaling.scaleUpTo4K(610, props.$vwWidth)}px;
        }
      }
    `;

const StyleCarddHeadingWithCloseButton = styled(Heading as any)`
    flex:1 0 auto;
    margin-left: ${props => Scaling.scaleUpTo4K(40, props.$vwWidth)}px;
    @media screen and (max-width:900px) {
      margin-left: 10px;
    }
  `;

interface ButtonWrapperProps {
  $dynamicFontSize: boolean
  $vwWidth: number
}

const StyledDiv = styled('div', {
  shouldForwardProp: (propName) => !propName.toString().startsWith('$')
}) <ButtonWrapperProps>`
  display: flex;
  flex-direction: row;
  height: 100%;
  justify-content: space-between;
  button {
    max-width: 40%;
    height: ${props => props.$dynamicFontSize ? (Scaling.scaleUpTo4K(64, props.$vwWidth) + 'px') : '64px'};
  }
`;

const formVariants = (vwWidth: number) => ({
  hidden: {
    opacity: 0,
    y: Scaling.scaleUpTo4K(50, vwWidth),
    transition: {
    },
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1,
      opacity: {
        duration: 0.68,
        delay: 0.3,
      },
      y: {
        delay: 0.3,
        type: 'spring',
        stiffness: 40,
      }
    },
  }
})

const formInnerVariants = {
  hidden: {
    opacity: 0,
    y: '20%',
    transition: {
      duration: 0.12,
      y: {
        delay: 0.12,
      },
    },
    transitionEnd: {
      zIndex: 0,
    },
  },
  visible: {
    opacity: 1,
    y: 0,
    zIndex: 1,
    transition: {
      type: 'spring',
      stiffness: 30,
      mass: 0.2,
      opacity: {
        delay: 0.22,
        duration: 0.18,
      },
    },
  },
};

interface Props {
  onChangeCreation: (event: ChangeEvent<HTMLInputElement> | Date | null) => void;
  onBlurLicensePlate: () => void;
  onBlurDamageDate: () => void;
  onConfirmCarInfo: () => void
  handleResetCreation: () => void;
  shouldShowError: (name: InputName, blurred: boolean) => boolean;
  showError: (name: InputName, blurred: boolean) => ReactNode
  formDataCreation: FormDataCreation;
  dateCreationBlurred: boolean;
  licenseCreationBlurred: boolean;
  errors: Record<string, string> | null;
  content: ContentStructureFormBlock;
  isLoading: boolean
}

const FormIntakePlus: FC<Props> = ({ isLoading, onChangeCreation, onConfirmCarInfo, onBlurLicensePlate, onBlurDamageDate, handleResetCreation, shouldShowError, showError, formDataCreation, licenseCreationBlurred, dateCreationBlurred, errors, content }) => {

  const caseDetails = useSelector((state: RootState) => state.caseDetails)
  const contentCardOne = content['card-one']
  const contentCardTwo = content['card-two']

  const { vwWidth } = useWindowSize();

  const { button: buttonCardOne, title: titleCardOne } = contentCardOne
  const { button: buttonCardTwo, buttonNo: buttonCardTwoNo, title: titleCardTwo } = contentCardTwo

  const { licensePlateCreation, damageDateCreation } = formDataCreation;
  return (
    <FormCard
      className="card-form single-card"
      shadow={4}
      variants={formVariants(vwWidth)}
      initial="hidden"
      animate="visible"
      $vwWidth={vwWidth}
    >
      <FormCardContainer
        className="card-form__container--response"
        flexDirection="column"
        flexGrow="1"
        flexShrink="1"
        width="100%"
        variants={formInnerVariants}
        initial={false}
        animate={!caseDetails.licensePlate ? 'visible' : 'hidden'}
        $vwWidth={vwWidth}
      >
        <Container
          className="card-form__header-group"
          flexGrow="0"
          margin="0px"
          alignItems="center"
          justifyContent="center"
        >
          {titleCardOne && <StyleCarddHeadingWithCloseButton
            level={4}
            textAlign="center"
            $vwWidth={vwWidth}
          >
            {titleCardOne}
          </StyleCarddHeadingWithCloseButton>}
        </Container>
        <CaseForm
          $vwWidth={vwWidth}
        >
          <LicenseInput
            onChange={onChangeCreation}
            value={licensePlateCreation}
            name="licensePlateCreation"
            type="text"
            label="Kenteken"
            onBlur={onBlurLicensePlate}
            error={shouldShowError('licensePlateCreation', licenseCreationBlurred)}
            dynamicFontSize={true}
          />
          {showError('licensePlateCreation', licenseCreationBlurred)}
          <DatePicker
            value={damageDateCreation || ''}
            onChange={onChangeCreation}
            label="Schadedatum"
            onBlur={onBlurDamageDate}
            error={shouldShowError('damageDateCreation', dateCreationBlurred)}
            dynamicFontSize={true}
          />
          {showError('damageDateCreation', dateCreationBlurred)}
          <SubmitButton
            value="Jouw opties"
            disabled={!!errors}
            dynamicFontSize={true}
            loading={isLoading}
          ><span>{buttonCardOne || 'Schadedossier aanmaken'}</span>
          </SubmitButton>
        </CaseForm>
      </FormCardContainer>
      <FormCardContainer
        className="card-form__container--response"
        flexDirection="column"
        flexGrow="1"
        flexShrink="1"
        width="100%"
        variants={formInnerVariants}
        initial={false}
        animate={caseDetails.licensePlate ? 'visible' : 'hidden'}
        $vwWidth={vwWidth}
      >
        <Container
          className="card-form__header-group"
          flexGrow="0"
          margin="0px"
          alignItems="center"
          justifyContent="center"
        >
          {titleCardTwo && <StyleCarddHeadingWithCloseButton
            level={4}
            textAlign="center"
            $vwWidth={vwWidth}
          >
            {titleCardTwo}
          </StyleCarddHeadingWithCloseButton>}
        </Container>
        <CaseForm
          $vwWidth={vwWidth}
        >{caseDetails.vehicleManufacturerName ?
          <Stack width={'100%'} paddingTop={'30px'} height={'100%'} display={'flex'} flexDirection={'column'} justifyContent={'space-between'}>
            <Input
              placeholder=""
              name="vehicleManufacturerName"
              onChange={onChangeCreation}
              type="text"
              disabled={true}
              label="Merk"
              error={false}
              value={caseDetails.vehicleManufacturerName || ''}
              dynamicFontSize={true}
            />
            <Input
              placeholder=""
              name="vehicleModelName"
              onChange={onChangeCreation}
              type="text"
              disabled={true}
              label="Model"
              error={false}
              value={caseDetails.vehicleModelName || ''}
              dynamicFontSize={true}
            />
          </Stack> : <Paragraph level={4} paddingTop={'30%'}>We konden uw voertuiggevens niet vinden. Druk op "Ja" om alsnog uw schade te melden.</Paragraph>}
          <StyledDiv $dynamicFontSize={true} $vwWidth={vwWidth}>
            <Button
              variant="outline"
              onClick={handleResetCreation}
            >
              <span>{buttonCardTwoNo || 'Nee'}</span>
            </Button>
            <Button
              onClick={onConfirmCarInfo}
              disabled={!!errors}
            ><span>{buttonCardTwo || 'Ja'}</span>
            </Button>
          </StyledDiv>
        </CaseForm>
      </FormCardContainer>
    </FormCard>
  )
};
export default FormIntakePlus
